import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTrackNextDataPageToken } from '../historyData/useTrackNextPageToken';
import DataApiClient from 'src/utils/DataApiClient';
import { useCallback, useContext, useEffect, useMemo } from 'react';

import {
	Serializer,
	UISerializablePositionHistoryRecord,
} from '@velocity-exchange/common';
import { DriftBlockchainEventSubjectContext } from 'src/providers/driftEvents/driftEventSubjectProvider';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';
import { JsonObject } from 'cerializr';
import {
	BASE_PRECISION_EXP,
	BigNum,
	PublicKey,
	QUOTE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import useLazySubAccounts from '../useLazySubAccounts';

type PositionHistoryQueryResult = {
	records: UISerializablePositionHistoryRecord[];
	nextPage: string | null;
};

export const usePositionHistory = (currentApiPage: number) => {
	const currentAccountPubKey = useDriftAccountsStore(
		(s) => s.getCurrentUserAccount()?.pubKey
	);
	const queryClient = useQueryClient();
	const { pageTokens, addPageToken } = useTrackNextDataPageToken();
	const eventSubjectContext = useContext(DriftBlockchainEventSubjectContext);
	const accounts = useLazySubAccounts();

	const findAccountToAppendTo = useCallback(
		(pubKey: PublicKey): string | undefined => {
			if (!accounts?.length || !pubKey) return;
			return accounts.find((acct) => acct?.pubKey?.equals(pubKey))?.userKey;
		},
		[accounts]
	);

	// Get previous page's token for the next page
	const pageToken = pageTokens.get(currentApiPage - 1);

	const queryKey = useMemo(
		() => ['positionHistory', currentApiPage, currentAccountPubKey?.toBase58()],
		[currentApiPage, currentAccountPubKey?.toBase58()]
	);

	const queryRes = useQuery<PositionHistoryQueryResult>({
		queryKey,
		queryFn: () => {
			return DataApiClient.fetchShortTermData<JsonObject>(
				'positionHistory',
				pageToken,
				{
					userAccount: currentAccountPubKey?.toBase58(),
				}
			).then((res) => {
				if (res.meta.nextPage) {
					addPageToken(currentApiPage, res.meta.nextPage);
				}
				return {
					records: res.records.map((r) =>
						Serializer.Deserialize.DataApiUISerializablePositionHistoryRecord(r)
					),
					nextPage: res.meta.nextPage,
				};
			});
		},
		staleTime: 15_000,
		refetchOnMount: true,
		refetchInterval: 30_000,
	});

	useEffect(() => {
		const globalFillEventSubscription =
			eventSubjectContext.globalFillEventObservable?.subscribe((fillEvent) => {
				const matchingTaker = fillEvent.taker
					? findAccountToAppendTo(fillEvent.taker)
					: undefined;
				const matchingMaker = fillEvent.maker
					? findAccountToAppendTo(fillEvent.maker)
					: undefined;

				const matchingUserKey =
					matchingTaker !== undefined
						? matchingTaker
						: matchingMaker !== undefined
							? matchingMaker
							: undefined;

				if (!matchingUserKey) {
					return;
				}

				queryClient.setQueryData<PositionHistoryQueryResult>(
					queryKey,
					(oldData) => {
						if (!oldData) return oldData;

						const serializedOrderActionRecord =
							Serializer.Serialize.OrderActionRecord(fillEvent);
						const formattedOrderActionRecord =
							Serializer.Deserialize.UIOrderActionRecord(
								serializedOrderActionRecord
							);

						const isTaker =
							currentAccountPubKey &&
							formattedOrderActionRecord.taker?.equals(currentAccountPubKey);
						const userOrderId = isTaker
							? formattedOrderActionRecord.takerOrderId
							: formattedOrderActionRecord.makerOrderId;

						// Find any existing records with the same orderId
						const existingRecordIndex = oldData.records.findIndex((record) => {
							const orderIdToMatch = isTaker
								? record.takerOrderId
								: record.makerOrderId;
							return orderIdToMatch === userOrderId;
						});

						// If found, merge the new fill with the existing record
						if (existingRecordIndex >= 0) {
							const updatedRecords = [...oldData.records];
							const existingRecord = updatedRecords[existingRecordIndex];

							// Update cumulative amounts
							const existingBaseAssetAmount = isTaker
								? existingRecord?.takerExistingBaseAssetAmount
								: existingRecord?.makerExistingBaseAssetAmount;
							const updatedRecord = {
								...existingRecord,
								quoteAssetAmountFilled:
									existingRecord.quoteAssetAmountFilled?.add(
										formattedOrderActionRecord.quoteAssetAmountFilled ??
											BigNum.zero(QUOTE_PRECISION_EXP)
									),
								baseAssetAmountFilled:
									existingRecord.baseAssetAmountFilled?.add(
										formattedOrderActionRecord.baseAssetAmountFilled ??
											BigNum.zero(BASE_PRECISION_EXP)
									),
								baseClosedForPnl: existingRecord.baseClosedForPnl?.add(
									existingBaseAssetAmount ??
										formattedOrderActionRecord.baseAssetAmountFilled ??
										BigNum.zero(BASE_PRECISION_EXP)
								),
								userFee: existingRecord.userFee?.add(
									isTaker
										? (formattedOrderActionRecord?.takerFee ??
												BigNum.zero(QUOTE_PRECISION_EXP))
										: (formattedOrderActionRecord?.makerFee ??
												BigNum.zero(QUOTE_PRECISION_EXP))
								),
								takerExistingQuoteEntryAmount:
									existingRecord.takerExistingQuoteEntryAmount?.add(
										formattedOrderActionRecord.takerExistingQuoteEntryAmount ??
											BigNum.zero(QUOTE_PRECISION_EXP)
									) ?? null,
								makerExistingQuoteEntryAmount:
									existingRecord.makerExistingQuoteEntryAmount?.add(
										formattedOrderActionRecord.makerExistingQuoteEntryAmount ??
											BigNum.zero(QUOTE_PRECISION_EXP)
									) ?? null,
								takerExistingBaseAssetAmount:
									existingRecord.takerExistingBaseAssetAmount?.add(
										formattedOrderActionRecord.takerExistingBaseAssetAmount ??
											BigNum.zero(BASE_PRECISION_EXP)
									) ?? null,
								makerExistingBaseAssetAmount:
									existingRecord.makerExistingBaseAssetAmount?.add(
										formattedOrderActionRecord.makerExistingBaseAssetAmount ??
											BigNum.zero(BASE_PRECISION_EXP)
									) ?? null,
							};

							updatedRecords[existingRecordIndex] = updatedRecord;

							return {
								...oldData,
								records: updatedRecords,
							};
						}

						return {
							...oldData,
							records: [
								{
									...formattedOrderActionRecord,
									baseClosedForPnl: BigNum.zero(BASE_PRECISION_EXP),
									userFee: BigNum.zero(QUOTE_PRECISION_EXP),
									user: currentAccountPubKey,
									symbol: '',
									eventType: 'PositionHistoryRecord',
								} as UISerializablePositionHistoryRecord,
								...oldData.records,
							],
						};
					}
				);
			});

		return () => globalFillEventSubscription?.unsubscribe();
	}, [
		eventSubjectContext,
		queryClient,
		queryKey,
		currentAccountPubKey?.toBase58(),
	]);

	return queryRes;
};
